import React from 'react';

function URLSchemeContent() {
  return (
    <>
      <p>RepoPrompt supports a flexible URL scheme that allows you to open projects, workspaces, specific files, and pre-fill prompts directly from external applications or shortcuts.</p>
      
      <h3>URL Scheme Format</h3>
      <p>The basic format for RepoPrompt's URL scheme is:</p>
      <pre>repoprompt://open/[folderPath]?[parameters]</pre>
      
      <h3>Components</h3>
      <ul>
        <li><strong>folderPath (Optional):</strong> Appears immediately after repoprompt://open/. This can be:
          <ul>
            <li>An absolute path (/Users/...)</li>
            <li>A path containing ~ for the home directory</li>
            <li>May contain percent-encoded characters (e.g. %20 for spaces)</li>
          </ul>
        </li>
        <li><strong>Query Parameters:</strong> Follow ? and can be combined with &
          <ul>
            <li><strong>workspace:</strong> Opens or creates a workspace named "MyWorkspace"</li>
            <li><strong>files:</strong> Comma-separated list of files to select in the file tree</li>
            <li><strong>prompt:</strong> Pre-fills the prompt text in the main prompt area or chat</li>
            <li><strong>ephemeral:</strong> When set to true, creates a temporary workspace that isn't saved</li>
            <li><strong>persist:</strong> When set to false, prevents saving the workspace to disk</li>
            <li><strong>focus:</strong> When set to true, attempts to focus an existing window with the target workspace</li>
          </ul>
        </li>
      </ul>
      
      <h3>Examples</h3>
      
      <h4>Open a specific folder:</h4>
      <pre>repoprompt://open/~/Documents/MyProject</pre>
      
      <h4>Open a workspace named "Personal":</h4>
      <pre>repoprompt://open?workspace=Personal</pre>
      
      <h4>Open a folder and select specific files:</h4>
      <pre>repoprompt://open/~/Projects?files=README.md,script.sh</pre>
      
      <h4>Open a workspace and pre-fill prompt text:</h4>
      <pre>repoprompt://open?workspace=Work&prompt=Review%20the%20latest%20updates</pre>
      
      <h4>Create a temporary workspace that won't be saved:</h4>
      <pre>repoprompt://open/~/Dev/TempProject?ephemeral=true</pre>
      
      <h4>Focus an existing window with the target workspace:</h4>
      <pre>repoprompt://open?workspace=Demo&focus=true</pre>
      
      <h4>Full example with folder, workspace, files, and prompt:</h4>
      <pre>repoprompt://open/~/Code?workspace=Demo&files=main.swift,AppDelegate.swift&prompt=Implement%20authentication%20logic&focus=true</pre>
      
      <h3>Important Notes</h3>
      <ul>
        <li>Spaces or special characters must be percent-encoded (e.g., spaces become %20)</li>
        <li>You can specify multiple files parameters or a comma-separated list for multiple files</li>
        <li>Multiple prompt parameters will concatenate into one message</li>
        <li>If ephemeral=true or persist=false, the workspace will not be saved to disk</li>
        <li>focus=true tries to focus an existing window with the target workspace</li>
        <li>Tilde (~) is automatically expanded to your home directory in all paths</li>
      </ul>
      
      <h3>Use Cases</h3>
      <p>The URL scheme is particularly useful for:</p>
      <ul>
        <li>Creating desktop shortcuts to frequently used workspaces</li>
        <li>Integration with automation tools like Shortcuts, Alfred, or Keyboard Maestro</li>
        <li>Linking to specific project configurations from documentation</li>
        <li>Setting up custom launch configurations for different development tasks</li>
      </ul>
      
      <div className="tip-box">
        <strong>Tip:</strong> Create Shortcuts on macOS to quickly open specific project configurations with pre-selected files and prompt text. This can dramatically speed up repetitive development tasks.
      </div>
    </>
  );
}

export default URLSchemeContent;